/**
 * Database Helper for GreenTech Shredding Route Planner
 * SQLite database operations for routes and call scripts
 *
 * Note: This is a browser-compatible implementation using sql.js (SQLite compiled to WebAssembly)
 * For Node.js backend, replace with better-sqlite3 or similar
 */

// Initialize database in browser using sql.js
let db = null;
const DB_NAME = 'greentech_planner';

/**
 * Initialize the database
 * Creates tables if they don't exist
 */
export async function initDatabase() {
    try {
        // Check if we're in a browser environment
        if (typeof window === 'undefined') {
            console.error('Database operations require browser environment');
            return false;
        }

        // Use IndexedDB for persistence in browser
        const SQL = await initSqlJs({
            locateFile: file => `https://sql.js.org/dist/${file}`
        });

        // Try to load existing database from localStorage
        const savedDb = localStorage.getItem(DB_NAME);
        if (savedDb) {
            const binaryArray = new Uint8Array(JSON.parse(savedDb));
            db = new SQL.Database(binaryArray);
        } else {
            db = new SQL.Database();
            // Create tables using schema
            await executeSchema();
        }

        console.log('Database initialized successfully');
        return true;
    } catch (error) {
        console.error('Error initializing database:', error);
        return false;
    }
}

/**
 * Execute the schema to create tables
 */
async function executeSchema() {
    const schema = `
        CREATE TABLE IF NOT EXISTS saved_routes (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            name TEXT NOT NULL UNIQUE,
            date_created DATETIME DEFAULT CURRENT_TIMESTAMP,
            date_modified DATETIME DEFAULT CURRENT_TIMESTAMP,
            jobs_json TEXT NOT NULL,
            is_optimized BOOLEAN DEFAULT 0,
            job_count INTEGER DEFAULT 0
        );

        CREATE TABLE IF NOT EXISTS call_scripts (
            id INTEGER PRIMARY KEY AUTOINCREMENT,
            script_name TEXT NOT NULL UNIQUE,
            script_content TEXT NOT NULL,
            script_type TEXT DEFAULT 'general',
            last_updated DATETIME DEFAULT CURRENT_TIMESTAMP
        );

        CREATE TABLE IF NOT EXISTS app_settings (
            key TEXT PRIMARY KEY,
            value TEXT,
            last_updated DATETIME DEFAULT CURRENT_TIMESTAMP
        );

        INSERT OR IGNORE INTO call_scripts (script_name, script_content, script_type) VALUES (
            'Default Lead Follow-up',
            'Hi {name}, this is {your_name} from GreenTech Shredding.

I''m calling to follow up on the proposal we sent for your shredding service. Have you had a chance to review it?

[Listen to response]

Great! I''d like to schedule your service. What dates work best for you?

Service Details:
- Material Type: {material_type}
- Frequency: {frequency}
- Number of Boxes: {number_of_boxes}
- Quoted Amount: {quoted}

[Schedule and confirm details]

We appreciate your business and look forward to serving you!',
            'lead_followup'
        );

        INSERT OR IGNORE INTO app_settings (key, value) VALUES ('default_script_id', '1');
        INSERT OR IGNORE INTO app_settings (key, value) VALUES ('items_per_page', '20');
    `;

    db.run(schema);
    saveDatabase();
}

/**
 * Save database to localStorage
 */
function saveDatabase() {
    if (!db) return;
    const data = db.export();
    const buffer = Array.from(data);
    localStorage.setItem(DB_NAME, JSON.stringify(buffer));
}

// ==================== SAVED ROUTES ====================

/**
 * Save a route to the database
 * @param {string} name - Route name (e.g., "Today", "Oct 10th")
 * @param {Array} jobs - Array of job objects
 * @param {boolean} isOptimized - Whether the route is optimized
 * @returns {Object} - { success, id, error }
 */
export function saveRoute(name, jobs, isOptimized = false) {
    try {
        const jobsJson = JSON.stringify(jobs);
        const jobCount = jobs.length;

        const stmt = db.prepare(
            `INSERT INTO saved_routes (name, jobs_json, is_optimized, job_count, date_created, date_modified)
             VALUES (?, ?, ?, ?, datetime('now'), datetime('now'))`
        );

        stmt.run([name, jobsJson, isOptimized ? 1 : 0, jobCount]);
        stmt.free();

        saveDatabase();

        return { success: true, id: db.exec("SELECT last_insert_rowid()")[0].values[0][0] };
    } catch (error) {
        console.error('Error saving route:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Update an existing route
 * @param {number} id - Route ID
 * @param {string} name - Route name
 * @param {Array} jobs - Array of job objects
 * @param {boolean} isOptimized - Whether the route is optimized
 * @returns {Object} - { success, error }
 */
export function updateRoute(id, name, jobs, isOptimized = false) {
    try {
        const jobsJson = JSON.stringify(jobs);
        const jobCount = jobs.length;

        const stmt = db.prepare(
            `UPDATE saved_routes
             SET name = ?, jobs_json = ?, is_optimized = ?, job_count = ?, date_modified = datetime('now')
             WHERE id = ?`
        );

        stmt.run([name, jobsJson, isOptimized ? 1 : 0, jobCount, id]);
        stmt.free();

        saveDatabase();

        return { success: true };
    } catch (error) {
        console.error('Error updating route:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Get all saved routes
 * @returns {Array} - Array of route objects
 */
export function getAllRoutes() {
    try {
        const result = db.exec(
            `SELECT id, name, date_created, date_modified, is_optimized, job_count
             FROM saved_routes
             ORDER BY date_modified DESC`
        );

        if (result.length === 0) return [];

        const rows = result[0].values;
        return rows.map(row => ({
            id: row[0],
            name: row[1],
            dateCreated: row[2],
            dateModified: row[3],
            isOptimized: row[4] === 1,
            jobCount: row[5]
        }));
    } catch (error) {
        console.error('Error getting routes:', error);
        return [];
    }
}

/**
 * Get a specific route by ID
 * @param {number} id - Route ID
 * @returns {Object|null} - Route object with jobs or null
 */
export function getRoute(id) {
    try {
        const stmt = db.prepare(
            `SELECT id, name, date_created, date_modified, jobs_json, is_optimized, job_count
             FROM saved_routes WHERE id = ?`
        );

        stmt.bind([id]);
        const hasRow = stmt.step();

        if (!hasRow) {
            stmt.free();
            return null;
        }

        const row = stmt.getAsObject();
        stmt.free();

        return {
            id: row.id,
            name: row.name,
            dateCreated: row.date_created,
            dateModified: row.date_modified,
            jobs: JSON.parse(row.jobs_json),
            isOptimized: row.is_optimized === 1,
            jobCount: row.job_count
        };
    } catch (error) {
        console.error('Error getting route:', error);
        return null;
    }
}

/**
 * Delete a route
 * @param {number} id - Route ID
 * @returns {Object} - { success, error }
 */
export function deleteRoute(id) {
    try {
        const stmt = db.prepare(`DELETE FROM saved_routes WHERE id = ?`);
        stmt.run([id]);
        stmt.free();

        saveDatabase();

        return { success: true };
    } catch (error) {
        console.error('Error deleting route:', error);
        return { success: false, error: error.message };
    }
}

// ==================== CALL SCRIPTS ====================

/**
 * Get all call scripts
 * @returns {Array} - Array of script objects
 */
export function getAllScripts() {
    try {
        const result = db.exec(
            `SELECT id, script_name, script_content, script_type, last_updated
             FROM call_scripts
             ORDER BY script_name`
        );

        if (result.length === 0) return [];

        const rows = result[0].values;
        return rows.map(row => ({
            id: row[0],
            name: row[1],
            content: row[2],
            type: row[3],
            lastUpdated: row[4]
        }));
    } catch (error) {
        console.error('Error getting scripts:', error);
        return [];
    }
}

/**
 * Get a specific call script
 * @param {number} id - Script ID
 * @returns {Object|null} - Script object or null
 */
export function getScript(id) {
    try {
        const stmt = db.prepare(
            `SELECT id, script_name, script_content, script_type, last_updated
             FROM call_scripts WHERE id = ?`
        );

        stmt.bind([id]);
        const hasRow = stmt.step();

        if (!hasRow) {
            stmt.free();
            return null;
        }

        const row = stmt.getAsObject();
        stmt.free();

        return {
            id: row.id,
            name: row.script_name,
            content: row.script_content,
            type: row.script_type,
            lastUpdated: row.last_updated
        };
    } catch (error) {
        console.error('Error getting script:', error);
        return null;
    }
}

/**
 * Save or update a call script
 * @param {string} name - Script name
 * @param {string} content - Script content
 * @param {string} type - Script type (default: 'general')
 * @param {number|null} id - Script ID (null for new script)
 * @returns {Object} - { success, id, error }
 */
export function saveScript(name, content, type = 'general', id = null) {
    try {
        if (id) {
            // Update existing script
            const stmt = db.prepare(
                `UPDATE call_scripts
                 SET script_name = ?, script_content = ?, script_type = ?, last_updated = datetime('now')
                 WHERE id = ?`
            );
            stmt.run([name, content, type, id]);
            stmt.free();

            saveDatabase();
            return { success: true, id };
        } else {
            // Insert new script
            const stmt = db.prepare(
                `INSERT INTO call_scripts (script_name, script_content, script_type, last_updated)
                 VALUES (?, ?, ?, datetime('now'))`
            );
            stmt.run([name, content, type]);
            stmt.free();

            saveDatabase();
            const newId = db.exec("SELECT last_insert_rowid()")[0].values[0][0];
            return { success: true, id: newId };
        }
    } catch (error) {
        console.error('Error saving script:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Delete a call script
 * @param {number} id - Script ID
 * @returns {Object} - { success, error }
 */
export function deleteScript(id) {
    try {
        const stmt = db.prepare(`DELETE FROM call_scripts WHERE id = ?`);
        stmt.run([id]);
        stmt.free();

        saveDatabase();

        return { success: true };
    } catch (error) {
        console.error('Error deleting script:', error);
        return { success: false, error: error.message };
    }
}

// ==================== APP SETTINGS ====================

/**
 * Get a setting value
 * @param {string} key - Setting key
 * @returns {string|null} - Setting value or null
 */
export function getSetting(key) {
    try {
        const stmt = db.prepare(`SELECT value FROM app_settings WHERE key = ?`);
        stmt.bind([key]);
        const hasRow = stmt.step();

        if (!hasRow) {
            stmt.free();
            return null;
        }

        const row = stmt.getAsObject();
        stmt.free();

        return row.value;
    } catch (error) {
        console.error('Error getting setting:', error);
        return null;
    }
}

/**
 * Set a setting value
 * @param {string} key - Setting key
 * @param {string} value - Setting value
 * @returns {Object} - { success, error }
 */
export function setSetting(key, value) {
    try {
        const stmt = db.prepare(
            `INSERT OR REPLACE INTO app_settings (key, value, last_updated)
             VALUES (?, ?, datetime('now'))`
        );
        stmt.run([key, value]);
        stmt.free();

        saveDatabase();

        return { success: true };
    } catch (error) {
        console.error('Error setting value:', error);
        return { success: false, error: error.message };
    }
}

// Note: For browser implementation, you'll need to include sql.js library
// Add to HTML: <script src="https://sql.js.org/dist/sql-wasm.js"></script>
// Or install via npm: npm install sql.js
